import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import ProjectsPage from "./ProjectsPage";
import DatePicker, { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useLocation } from "react-router-dom";
import { getUserRoleFromToken } from "../utils/getUserRoleFromToken";
import TruncateWithTooltip from "../components/TruncateWithTooltip";

registerLocale("en-GB", enGB);

const API = "/api";

const TABS = [
  { key: "projects", label: "Projects" },
  { key: "time", label: "Time Entries" },
  { key: "work", label: "Work Entries" },
];

const pad2 = (n) => String(n).padStart(2, "0");
const formatLocalYMD = (d) =>
  d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : undefined;
const addDays = (d, n) =>
  d ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + n) : undefined;

function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()} ${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
}
function formatDuration(start, end) {
  if (!start || !end) return "-";
  const ms = new Date(end) - new Date(start);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}
function durationMs(start, end) {
  if (!start || !end) return null;
  return new Date(end).getTime() - new Date(start).getTime();
}
function userKey(row) {
  const fn = (row.first_name || "").trim();
  const ln = (row.last_name || "").trim();
  const em = (row.email || "").trim();
  return `${ln}, ${fn} (${em})`.toLowerCase();
}

function ClickableName({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 text-blue-700 underline cursor-pointer focus:outline-none"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <span aria-hidden="true">{icon}</span>
      <TruncateWithTooltip text={label} />
    </button>
  );
}

function AdminWorkersPage() {
  const token = localStorage.getItem("token");
  const userRole = getUserRoleFromToken(token);
  const isAdmin = userRole === "admin";
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin) navigate("/");
  }, [isAdmin, navigate]);

  const [tab, setTab] = useState(() => window.localStorage.getItem("adminTab") || "projects");

  // Users / filters
  const [allUsers, setAllUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);

  // Sorting per-tab
  const [sort, setSort] = useState({ col: "clock_in", order: "desc" });

  // Data
  const [entries, setEntries] = useState([]); // time
  const [workEntries, setWorkEntries] = useState([]); // work
  const [loadingEntries, setLoadingEntries] = useState(false);

  // Jump from tables to Projects tab
  const [navigateTo, setNavigateTo] = useState({ projectId: null, fileId: null, folderId: null });

  const jumpToProjects = (target) => {
    setNavigateTo(target);
    setTimeout(() => setTab("projects"), 0);
  };

  useEffect(() => {
    if (
      location.state &&
      (location.state.projectId !== undefined ||
        location.state.fileId !== undefined ||
        location.state.folderId !== undefined)
    ) {
      setTab("projects");
      setNavigateTo({
        projectId: location.state.projectId ?? null,
        fileId: location.state.fileId ?? null,
        folderId: location.state.folderId ?? null,
      });
      if (location.state.ownerId != null) {
        setSelectedUser(String(location.state.ownerId));
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state]);

  useEffect(() => {
    window.localStorage.setItem("adminTab", tab);
    setSort(tab === "work" ? { col: "start_time", order: "desc" } : { col: "clock_in", order: "desc" });
  }, [tab]);

  // fetch all users once
  useEffect(() => {
    setLoadingUsers(true);
    axios
      .get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setAllUsers(list);
        setUsers(list);
        setLoadingUsers(false);
      })
      .catch(() => setLoadingUsers(false));
  }, [token]);

  // search user
  useEffect(() => {
    const q = (userSearch || "").trim().toLowerCase();
    if (!q) {
      setUsers(allUsers);
      setSelectedUser("");
      return;
    }
    let best = null;
    let bestScore = Infinity;
    for (const u of allUsers) {
      const full = `${u.first_name || ""} ${u.last_name || ""}`.trim().toLowerCase();
      const email = (u.email || "").toLowerCase();
      let score = Infinity;
      const inFull = full.indexOf(q);
      const inEmail = email.indexOf(q);
      if (inFull === 0) score = Math.min(score, 0);
      if (inEmail === 0) score = Math.min(score, 0.5);
      if (inFull > 0) score = Math.min(score, 1 + inFull / 100);
      if (inEmail > 0) score = Math.min(score, 2 + inEmail / 100);
      if (score < Infinity && score < bestScore) {
        best = u;
        bestScore = score;
      }
    }
    if (best) {
      setSelectedUser(best.id);
      const narrowed = allUsers.filter((u) => {
        const fn = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
        const em = (u.email || "").toLowerCase();
        return fn.includes(q) || em.includes(q);
      });
      setUsers(narrowed.length ? narrowed : allUsers);
    } else {
      setUsers(allUsers);
      setSelectedUser("");
    }
  }, [userSearch, allUsers]);

  const userOptions = useMemo(
    () => [
      { value: "", label: "All users" },
      ...users.map((u) => ({
        value: u.id,
        label: `${u.first_name || ""} ${u.last_name || ""} (${u.email})`.trim(),
      })),
    ],
    [users]
  );

  // ---- Fetch entries (filters only; client-side sorting)
  useEffect(() => {
    if (tab === "time") {
      setLoadingEntries(true);
      axios
        .get(`${API}/time`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            user: selectedUser || "all", // ← IMPORTANT: 'all' flag for admins
            from: formatLocalYMD(dateRange[0]),
            to: formatLocalYMD(addDays(dateRange[1], 1)),
          },
        })
        .then((res) => {
          setEntries(Array.isArray(res.data) ? res.data : []);
          setLoadingEntries(false);
        })
        .catch(() => setLoadingEntries(false));
    } else if (tab === "work") {
      setLoadingEntries(true);
      axios
        .get(`${API}/work`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            user: selectedUser || "all", // ← IMPORTANT: 'all' flag for admins
            from: formatLocalYMD(dateRange[0]),
            to: formatLocalYMD(addDays(dateRange[1], 1)),
          },
        })
        .then((res) => {
          setWorkEntries(Array.isArray(res.data) ? res.data : []);
          setLoadingEntries(false);
        })
        .catch(() => setLoadingEntries(false));
    }
  }, [tab, selectedUser, dateRange, token]);

  const setSortCol = (col) => {
    setSort((s) => {
      const order = s.col === col ? (s.order === "asc" ? "desc" : "asc") : "asc";
      return { col, order };
    });
  };
  const headerClass = (col) =>
    `py-2 text-left font-semibold cursor-pointer select-none ${sort.col === col ? "text-blue-700" : ""}`;
  const arrow = (col) => (sort.col === col ? (sort.order === "asc" ? "▲" : "▼") : "");

  const sortedTimeEntries = useMemo(() => {
    const active = entries.filter((e) => !e.clock_out);
    const ended = entries.filter((e) => !!e.clock_out);
    const cmp = (a, b) => {
      let ax = 0,
        bx = 0;
      if (sort.col === "clock_in" || sort.col === "clock_out") {
        ax = a[sort.col] ? new Date(a[sort.col]).getTime() : 0;
        bx = b[sort.col] ? new Date(b[sort.col]).getTime() : 0;
      } else if (sort.col === "user") {
        const axS = userKey(a);
        const bxS = userKey(b);
        return sort.order === "asc" ? (axS < bxS ? -1 : axS > bxS ? 1 : 0) : (axS > bxS ? -1 : axS < bxS ? 1 : 0);
      } else if (sort.col === "duration") {
        ax = durationMs(a.clock_in, a.clock_out) ?? 0;
        bx = durationMs(b.clock_in, b.clock_out) ?? 0;
      }
      return sort.order === "asc" ? ax - bx : bx - ax;
    };
    return [...active.sort(cmp), ...ended.sort(cmp)];
  }, [entries, sort.col, sort.order]);

  const sortedWorkEntries = useMemo(() => {
    const active = workEntries.filter((e) => !e.end_time);
    const ended = workEntries.filter((e) => !!e.end_time);
    const cmp = (a, b) => {
      if (sort.col === "user") {
        const axS = userKey(a);
        const bxS = userKey(b);
        return sort.order === "asc" ? (axS < bxS ? -1 : axS > bxS ? 1 : 0) : (axS > bxS ? -1 : axS < bxS ? 1 : 0);
      }
      let ax = 0,
        bx = 0;
      if (sort.col === "start_time" || sort.col === "end_time") {
        ax = a[sort.col] ? new Date(a[sort.col]).getTime() : 0;
        bx = b[sort.col] ? new Date(b[sort.col]).getTime() : 0;
      } else if (sort.col === "duration") {
        ax = durationMs(a.start_time, a.end_time) ?? 0;
        bx = durationMs(b.start_time, b.end_time) ?? 0;
      }
      return sort.order === "asc" ? ax - bx : bx - ax;
    };
    return [...active.sort(cmp), ...ended.sort(cmp)];
  }, [workEntries, sort.col, sort.order]);

  const COL_TIME = "16rem";
  const COL_DURATION = "8rem";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isAdmin={isAdmin} />
      <div className="pt-20 max-w-6xl mx-auto px-4">
        <h2 className="text-2xl font-bold mb-6">Workers</h2>

        <div className="flex gap-6 mb-6 border-b pb-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`text-lg font-semibold px-4 py-2 border-b-2 ${
                tab === t.key ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500"
              }`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-4 items-end mb-4">
          <div>
            <label className="block font-semibold text-gray-700 mb-1">User</label>
            <select
              className="border border-gray-300 rounded p-2 bg-gray-100 w-64"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              {userOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-semibold text-gray-700 mb-1">Search user</label>
            <input
              type="text"
              className="border border-gray-300 rounded p-2 bg-gray-100 w-64"
              placeholder="Type a name or email"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          </div>
          {(tab === "time" || tab === "work") && (
            <div>
              <label className="block font-semibold text-gray-700 mb-1">Date Range</label>
              <DatePicker
                selectsRange
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onChange={(dates) => setDateRange(dates)}
                isClearable
                placeholderText="Pick date or range"
                className="border border-gray-300 rounded p-2 bg-gray-100 w-60"
                locale="en-GB"
                dateFormat="dd-MM-yyyy"
              />
            </div>
          )}
        </div>

        {tab === "projects" && (
          <div className="mt-6">
            <ProjectsPage
              isAdmin={true}
              adminUserId={selectedUser}
              adminMode={true}
              adminContext="workers"
              navigateTo={navigateTo}
              setNavigateTo={setNavigateTo}
              showOwnerColumn={true}
              fullWidth={true}
            />
          </div>
        )}

        {tab === "time" && (
          <div className="mt-8">
            <div className="bg-white rounded-lg shadow border">
              <table className="w-full">
                <colgroup>
                  <col style={{ width: COL_TIME }} />
                  <col style={{ width: COL_TIME }} />
                  <col /> {/* user flex */}
                  <col style={{ width: COL_DURATION }} />
                </colgroup>
                <thead className="bg-gray-100 border-b">
                  <tr className="text-gray-600">
                    <th className={`py-2 pl-4 ${headerClass("clock_in")}`} onClick={() => setSortCol("clock_in")}>
                      Clock In {arrow("clock_in")}
                    </th>
                    <th className={headerClass("clock_out")} onClick={() => setSortCol("clock_out")}>
                      Clock Out {arrow("clock_out")}
                    </th>
                    <th className={headerClass("user")} onClick={() => setSortCol("user")}>
                      User {arrow("user")}
                    </th>
                    <th className={headerClass("duration")} onClick={() => setSortCol("duration")}>
                      Duration {arrow("duration")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loadingEntries ? (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-gray-400">
                        Loading…
                      </td>
                    </tr>
                  ) : sortedTimeEntries.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-gray-400">
                        No time entries found.
                      </td>
                    </tr>
                  ) : (
                    sortedTimeEntries.map((row) => (
                      <tr key={row.id} className="border-b last:border-b-0">
                        <td className="py-2 pl-4">{formatDateTime(row.clock_in)}</td>
                        <td className="py-2">
                          {row.clock_out ? (
                            formatDateTime(row.clock_out)
                          ) : (
                            <span className="text-green-700 font-semibold">Active</span>
                          )}
                        </td>
                        <td className="py-2">
                          <TruncateWithTooltip text={`${row.first_name} ${row.last_name} (${row.email})`} />
                        </td>
                        <td className="py-2">{formatDuration(row.clock_in, row.clock_out)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "work" && (
          <div className="mt-8">
            <div className="bg-white rounded-lg shadow border">
              <table className="w-full">
                <colgroup>
                  <col style={{ width: COL_TIME }} />
                  <col style={{ width: COL_TIME }} />
                  <col /> {/* user flex */}
                  <col /> {/* project/file flex */}
                  <col style={{ width: COL_DURATION }} />
                </colgroup>
                <thead className="bg-gray-100 border-b">
                  <tr className="text-gray-600">
                    <th className={`py-2 pl-4 ${headerClass("start_time")}`} onClick={() => setSortCol("start_time")}>
                      Start {arrow("start_time")}
                    </th>
                    <th className={headerClass("end_time")} onClick={() => setSortCol("end_time")}>
                      End {arrow("end_time")}
                    </th>
                    <th className={headerClass("user")} onClick={() => setSortCol("user")}>
                      User {arrow("user")}
                    </th>
                    <th className="py-2 text-left font-semibold">Project / File</th>
                    <th className={headerClass("duration")} onClick={() => setSortCol("duration")}>
                      Duration {arrow("duration")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loadingEntries ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-gray-400">
                        Loading…
                      </td>
                    </tr>
                  ) : sortedWorkEntries.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-gray-400">
                        No work entries found.
                      </td>
                    </tr>
                  ) : (
                    sortedWorkEntries.map((row) => (
                      <tr key={row.id} className="border-b last:border-b-0">
                        <td className="py-2 pl-4">{formatDateTime(row.start_time)}</td>
                        <td className="py-2">
                          {row.end_time ? (
                            formatDateTime(row.end_time)
                          ) : (
                            <span className="text-green-700 font-semibold">Active</span>
                          )}
                        </td>
                        <td className="py-2">
                          <TruncateWithTooltip text={`${row.first_name} ${row.last_name} (${row.email})`} />
                        </td>
                        <td className="py-2">
                          {row.file_id && row.file_name ? (
                            <ClickableName
                              icon="📄"
                              label={row.file_name}
                              onClick={() =>
                                jumpToProjects({
                                  projectId: row.project_id ?? row.project_parent_id ?? row.parent_id ?? null,
                                  fileId: row.file_id,
                                  folderId: null,
                                })
                              }
                            />
                          ) : row.project_id && row.project_name ? (
                            <ClickableName
                              icon="📁"
                              label={row.project_name}
                              onClick={() =>
                                jumpToProjects({
                                  projectId: row.parent_id ?? null,
                                  fileId: null,
                                  folderId: row.project_id,
                                })
                              }
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="py-2">{formatDuration(row.start_time, row.end_time)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminWorkersPage;
